/**
 * Merkezi auth yönetimi — tüm paneller tek session'a bakar.
 *
 * Politika:
 *  - localStorage anahtarı: `app_session` JSON: { token, refresh_token, role, roles[], username, display_name, login_at, remember_me }
 *  - Eski panel-bazlı session anahtarları (bobin_session, plan_session, vs.) artık
 *    yazılmıyor/silinmiyor (bkz. PANEL_SESSION_KEYS) — sadece geçiş toast'ı için okunur.
 *  - Oturum geçerliliği refresh_token'ın kendi JWT `exp` alanına bakılarak belirlenir
 *    (isSessionValid) — backend REFRESH_TOKEN_DAYS kadar (varsayılan 7 gün) geçerli;
 *    access token (30dk) süresi dolduğunda arka planda sessizce yenilenir (bkz.
 *    lib/authRefresh.js). Önceden remember_me=false için ayrı, sahte bir 24 saatlik
 *    istemci-taraf sınır vardı (login_at + 24h) — bu KALDIRILDI: arka planda sessiz
 *    yenileme çalışırken kullanıcı günde bir zorla login'e düşüyordu.
 *  - remember_me: SADECE istemci tarafında saklanan bir bayrak (kullanıcı adını
 *    hatırlama / UnifiedLogin checkbox varsayılan durumu için). Backend'de HİÇBİR
 *    karşılığı yok — /users/login bu alanı okumaz, refresh token süresini
 *    etkilemez (bkz. saveSession altındaki not). Gerçek "güvenilir cihaz" / uzun
 *    ömürlü oturum ileride ayrı bir özellik (trusted_devices) olarak ele alınacak.
 *  - Yönetim rolü: tüm panellere erişebilir.
 *  - Plan rolü: plan, warehouse, paint, bobin, marka-stok, driver.
 *  - Operator rolü: sadece operator.
 *  - Sofor rolü: sadece driver.
 */

export const SESSION_KEY = "app_session";
export const REMEMBER_USERNAME_KEY = "app_remember_username";

/** JWT payload'unu (ortadaki base64url parça) çözer. Geçersizse null döner.
 *  İmza DOĞRULAMASI yapmaz — sadece istemci tarafında görüntüleme/karar için
 *  (user_id çıkarma, exp kontrolü). Gerçek doğrulama her zaman backend'de. */
function decodeJwtPayload(token) {
  try {
    const parts = (token || "").split(".");
    if (parts.length !== 3) return null;
    return JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")));
  } catch {
    return null;
  }
}

// Panel ↔ izinli roller eşlemesi (her zaman yonetim'e açık)
export const ROUTE_ROLES = {
  "/management": ["yonetim"],
  "/operator":   ["yonetim", "operator"],
  "/plan":       ["yonetim", "plan"],
  "/boyaci":     ["yonetim", "boyaci"],
  "/warehouse":  ["yonetim", "plan", "depo"],
  "/paint":      ["yonetim", "plan", "depo", "boyaci"],
  "/bobin":      ["yonetim", "plan", "depo"],
  "/marka-stok": ["yonetim", "plan", "depo"],
  "/driver":     ["yonetim", "plan", "sofor"],
  "/kamera":     ["yonetim", "plan"],
  // Vardiya planı: düzenleme/olaylar backend'de yonetim+plan'a kısıtlı
  "/vardiya-plani": ["yonetim", "plan", "operator", "depo"],
  // Ortak fabrika tableti: tablet rolü SADECE burada (backend'de de varsayılan ret,
  // bkz. services/tablet.py); yönetim tabletin ne gösterdiğini kontrol edebilsin
  "/tablet":     ["tablet", "yonetim"],
  // Dashboard ve takip her zaman ayrı şifre ile (sabit) erişilir
};

/** Girişten sonra gidilecek adres. Varsayılan ana sayfa (panel kartları); ortak tablet
 *  her zaman /tablet. returnTo: oturum yokken açılmak istenen panel (bkz. ProtectedRoute) —
 *  yalnız bilinen ve bu rollerin girebildiği bir panelse ona dönülür. */
export function postLoginRoute(roles, returnTo) {
  if (roles.includes("tablet")) return "/tablet";
  const allowed = returnTo ? ROUTE_ROLES[returnTo.split(/[?#]/)[0]] : null;
  return allowed && roles.some((r) => allowed.includes(r)) ? returnTo : "/";
}

// Artık hiçbir kod bu anahtarları yazmıyor/silmiyor. Liste sadece
// hasLegacySessionArtifacts() için kalıyor: eski sürümlerden tarayıcıda
// kalmış olabilecek anahtarları tespit edip geçiş toast'ı göstermek amacıyla.
const PANEL_SESSION_KEYS = [
  "operator_session", "plan_session", "depo_session",
  "warehouse_session", "bobin_session", "yonetim_master",
  "paint_session", "marka_stok_session", "boyaci_session",
];

export function getSession() {
  try {
    const s = JSON.parse(localStorage.getItem(SESSION_KEY) || "null");
    if (!s || !s.token) return null;
    // Geriye dönük uyumluluk: eski session'larda user_id yoksa JWT'den çıkar
    if (!s.user_id && s.token) {
      const payload = decodeJwtPayload(s.token);
      if (payload) s.user_id = payload.sub || payload.user_id || payload.id || null;
    }
    return s;
  } catch {
    return null;
  }
}

/** Session geçerli mi? refresh_token'ın kendi JWT `exp`'ine bakar (bkz. dosya başı
 *  Politika notu) — flat `refresh_token` anahtarı tercih edilir çünkü yenileme
 *  akışı (lib/authRefresh.js) her başarılı rotation'da onu güncel tutar; o anahtar
 *  yoksa (ör. çok eski bir session) app_session içindeki alana düşülür. */
export function isSessionValid() {
  const s = getSession();
  if (!s) return false;
  let rt = null;
  try { rt = localStorage.getItem("refresh_token"); } catch { /* noop */ }
  rt = rt || s.refresh_token;
  if (!rt) return false;
  const payload = decodeJwtPayload(rt);
  return !!payload?.exp && payload.exp * 1000 > Date.now();
}

/** Rol dizisinde yönetim yetkisi var mı? (backend'deki is_yonetim ile tutarlı). */
export function hasYonetimRole(roles) {
  return !!roles && roles.includes("yonetim");
}

/** Kayıtlı oturum ortak fabrika tabletine mi ait? (tablet rolü tek başına verilir.)
 *  Oturumun geçerliliğine BAKMAZ — tablet token'ıyla gereksiz istek atmamak
 *  (ziyaretçi kaydı, makine init, sohbet) için; yönlendirmede isSessionValid ile
 *  birlikte kullanılır. */
export function isTabletSession() {
  const s = getSession();
  if (!s) return false;
  const roles = s.roles && s.roles.length ? s.roles : [s.role];
  return roles.includes("tablet");
}

/** Kullanıcının panele erişimi var mı? */
export function canAccessRoute(pathname) {
  const s = getSession();
  if (!s) return false;
  const allowed = ROUTE_ROLES[pathname];
  if (!allowed) return true; // koruma olmayan path
  const userRoles = (s.roles && s.roles.length ? s.roles : [s.role]).filter(Boolean);
  return userRoles.some((r) => allowed.includes(r));
}

/** Rolün erişebileceği route listesini döner (path) */
export function getAccessibleRoutes(rolesArg) {
  const s = getSession();
  const roles = rolesArg || (s ? (s.roles?.length ? s.roles : [s.role]) : []);
  return Object.entries(ROUTE_ROLES)
    .filter(([, allowed]) => roles.some((r) => allowed.includes(r)))
    .map(([path]) => path);
}

/** Login sonrası session yazımı + axios header güncelleme */
export function saveSession({
  token, refresh_token, role, roles, username, display_name, remember_me, user_id, id,
}) {
  // JWT'den user_id çıkar (varsa)
  let resolvedUserId = user_id || id || null;
  if (!resolvedUserId && token) {
    const payload = decodeJwtPayload(token);
    if (payload) resolvedUserId = payload.sub || payload.user_id || payload.id || null;
  }
  const session = {
    token,
    refresh_token: refresh_token || null,
    user_id: resolvedUserId,
    role: role || (roles && roles[0]) || "",
    roles: roles && roles.length ? roles : (role ? [role] : []),
    username: username || "",
    display_name: display_name || username || "",
    login_at: new Date().toISOString(),
    // remember_me: SADECE istemci tarafında bir bayrak — sunucu tarafında hiçbir
    // etkisi yok (bkz. dosya başı Politika notu). Şu an tek kullanım yeri: aşağıda
    // kullanıcı adını hatırlama (REMEMBER_USERNAME_KEY). Oturum geçerliliğine
    // artık karışmıyor (isSessionValid refresh_token'ın exp'ine bakıyor).
    remember_me: !!remember_me,
  };
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
    localStorage.setItem("auth_token", token); // axios interceptor için
    if (refresh_token) localStorage.setItem("refresh_token", refresh_token);
    if (remember_me && username) {
      localStorage.setItem(REMEMBER_USERNAME_KEY, username);
    }
    // Subscribers (GlobalMessenger vb.) anlık güncellensin
    try { window.dispatchEvent(new CustomEvent("auth-changed", { detail: { type: "login" } })); } catch (_) { /* noop */ }
    return session;
  } catch (e) {
    console.error("saveSession error", e);
    return null;
  }
}

export function clearSession() {
  if (typeof window !== "undefined") window.dispatchEvent(new Event("session-cleared"));
  try {
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem("auth_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("dashboard_token");
    sessionStorage.removeItem("dashboard_token");
    sessionStorage.removeItem("dashboard_session");
  } catch (_) {
    /* noop */
  }
  // Subscribers (GlobalMessenger, WebSocket vb.) anlık temizlensin
  try { window.dispatchEvent(new CustomEvent("auth-changed", { detail: { type: "logout" } })); } catch (_) { /* noop */ }
}

/** Eski panel-bazlı session anahtarlarından biri localStorage'da kaldıysa true.
 *  SADECE geçiş toast'ı için — erişim kararına karışmaz (ProtectedRoute artık
 *  tek doğruluk kaynağı olarak app_session'a bakar). */
export function hasLegacySessionArtifacts() {
  try {
    return [...PANEL_SESSION_KEYS, "management_session", "driver_session"].some(
      (k) => !!localStorage.getItem(k)
    );
  } catch (_) {
    return false;
  }
}

export function getRememberedUsername() {
  try {
    return localStorage.getItem(REMEMBER_USERNAME_KEY) || "";
  } catch (_) {
    return "";
  }
}

/**
 * Panellerin TEK doğruluk kaynağı.
 * Merkezi oturum geçerliyse (refresh_token'ın exp'i — bkz. isSessionValid) ve kullanıcı
 * verilen route'a erişebiliyorsa, panelde kullanılacak normalize kullanıcı
 * verisini döner. Aksi halde null döner.
 *
 * Yan etki: auth_token localStorage'da yoksa merkezi token'ı yazar
 * (mevcut taze token'ı ASLA ezmeden — interceptor refresh'i korunur).
 *
 * Bu sayede ana sayfada bir kez giriş yapan kullanıcı, rolünün eriştiği
 * panellerde tekrar şifre sormadan açılır.
 */
export function resumeCentralSession(routePath) {
  if (!isSessionValid()) return null;
  if (routePath && !canAccessRoute(routePath)) return null;
  const s = getSession();
  if (!s || !s.token) return null;
  try {
    if (!localStorage.getItem("auth_token")) {
      localStorage.setItem("auth_token", s.token);
    }
  } catch (_) { /* noop */ }
  const roles = (s.roles && s.roles.length) ? s.roles : (s.role ? [s.role] : []);
  return {
    token: s.token,
    username: s.username || "",
    display_name: s.display_name || s.username || "",
    roles,
    role: s.role || roles[0] || "",
    id: s.user_id || s.id || s.username || "",
    login_time: new Date(s.login_at || Date.now()).getTime(),
    remember_me: !!s.remember_me,
  };
}
